import React, { useEffect, useState } from "react";
import { api } from "../lib/api";
import { FileText, Download, X, Eye } from "lucide-react";
import { Button } from "../components/ui/button";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function Documents() {
  const [docs, setDocs] = useState([]);
  const [viewing, setViewing] = useState(null);

  useEffect(() => {
    api.get("/documents").then(({ data }) => setDocs(data)).catch(() => {});
  }, []);

  const fileUrl = (d) => `${BACKEND_URL}${d.public_url}`;

  return (
    <div className="fade-in max-w-7xl mx-auto px-4 md:px-6 py-12 md:py-16">
      <div className="max-w-3xl mb-10">
        <div className="text-xs uppercase tracking-[0.2em] text-[#138808] font-bold mb-2">Documents</div>
        <h1 className="serif text-4xl md:text-5xl font-semibold text-[#000080] leading-tight">
          Radical <em className="text-[#FF9933] not-italic">transparency</em>. Always.
        </h1>
        <p className="mt-4 text-lg text-[#4A4A4A]">
          Our registrations, annual reports, and audit statements — viewable live, downloadable any time.
        </p>
      </div>

      {docs.length === 0 ? (
        <div className="bg-white border border-dashed border-[#eae5d9] rounded-3xl p-16 text-center">
          <FileText className="w-10 h-10 text-[#FF9933] mx-auto mb-4" strokeWidth={1.4} />
          <p className="text-[#737373]">Documents will appear here as they are uploaded by the admin team.</p>
          <div className="grid md:grid-cols-3 gap-3 max-w-3xl mx-auto mt-10 text-left">
            {["80G Certificate","12A Certificate","NGO DARPAN Certificate","CSR Registration","PAN Certificate","Annual Report 2024-25"].map((d, i) => (
              <div key={i} className="bg-[#F3EFE7] rounded-xl p-4 border border-[#eae5d9]">
                <FileText className="w-5 h-5 text-[#FF9933] mb-2" strokeWidth={1.6} />
                <div className="text-sm font-semibold text-[#000080]">{d}</div>
                <div className="text-xs text-[#737373]">Coming soon</div>
              </div>
            ))}
          </div>
        </div>
      ) : (
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5">
          {docs.map((d) => (
            <div key={d.id} className="card-hover bg-white rounded-2xl border border-[#eae5d9] p-6" data-testid={`doc-${d.id}`}>
              <div className="w-12 h-12 rounded-xl bg-[#FF9933]/15 flex items-center justify-center mb-4">
                <FileText className="w-6 h-6 text-[#FF9933]" strokeWidth={1.6} />
              </div>
              <div className="serif text-xl font-semibold text-[#000080] leading-snug">{d.title}</div>
              {d.description && <div className="text-sm text-[#737373] mt-1.5">{d.description}</div>}
              <div className="text-xs text-[#737373] mt-3">Uploaded {new Date(d.created_at).toLocaleDateString("en-IN")}</div>
              <div className="mt-4 flex gap-2">
                <Button onClick={() => setViewing(d)} size="sm" className="bg-[#000080] hover:bg-[#000066] text-white rounded-full" data-testid={`doc-view-${d.id}`}>
                  <Eye className="w-3.5 h-3.5 mr-1.5" /> Live View
                </Button>
                <a href={fileUrl(d)} download target="_blank" rel="noreferrer" data-testid={`doc-download-${d.id}`}>
                  <Button size="sm" variant="outline" className="rounded-full border-[#eae5d9]">
                    <Download className="w-3.5 h-3.5 mr-1.5" /> Download
                  </Button>
                </a>
              </div>
            </div>
          ))}
        </div>
      )}

      {viewing && (
        <div className="fixed inset-0 z-50 bg-black/80 p-4 flex items-center justify-center" data-testid="doc-viewer">
          <div className="w-full max-w-5xl h-full max-h-[90vh] bg-white rounded-2xl overflow-hidden flex flex-col">
            <div className="flex items-center justify-between p-3 border-b border-[#eae5d9] bg-[#F3EFE7]">
              <div className="font-semibold text-[#000080] truncate px-2">{viewing.title}</div>
              <div className="flex items-center gap-2">
                <a href={fileUrl(viewing)} download target="_blank" rel="noreferrer">
                  <Button size="sm" variant="outline" className="rounded-full"><Download className="w-3.5 h-3.5 mr-1" /> Download</Button>
                </a>
                <button onClick={() => setViewing(null)} className="p-1.5"><X className="w-5 h-5" /></button>
              </div>
            </div>
            <iframe src={fileUrl(viewing)} title={viewing.title} className="flex-1 w-full border-0" />
          </div>
        </div>
      )}
    </div>
  );
}
